import { supabase } from '@/shared/lib/supabase';
import type {
  CustomerStatement,
  StatementTransaction,
  AgingAnalysis,
} from '../types/statement.types';

/**
 * Customer statement service — lifted from ledger-flow-ifrs-beacon
 * `domains/reporting/services/customerStatementService.ts` and adapted:
 *
 *   - Single-tenant: no companyId. The statement is always for `auth.uid()`.
 *   - Drops Hindsight's accounting_periods.opening_balances (OBE) lookup —
 *     SightHost has no chart of accounts; opening balance = sum of all prior
 *     transactions before fromDate.
 *   - RLS-scoped queries against invoice / payment / credit_note tables.
 *
 * Generates: opening balance, transaction list with running balance, aging
 * breakdown (6 buckets per IFRS), period totals.
 */

const ZERO_AGING: AgingAnalysis = {
  currentZAR: 0,
  days30ZAR: 0,
  days60ZAR: 0,
  days90ZAR: 0,
  days120ZAR: 0,
  over120ZAR: 0,
  totalZAR: 0,
};

const num = (v: number | string | null): number => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return v;
  return Number(v) || 0;
};

interface InvoiceLite {
  id: string;
  number: string;
  issue_date: string;
  due_date: string;
  status: string;
  total: number | string;
}

interface PaymentLite {
  id: string;
  invoice_id: string | null;
  paid_at: string | null;
  amount: number | string;
  status: string;
  gateway: string;
  gateway_transaction_id: string | null;
}

interface CreditNoteLite {
  id: string;
  number: string;
  issued_at: string;
  amount: number | string;
  related_invoice_id: string | null;
  reason: string;
}

export const statementService = {
  /**
   * Generate a statement for the authenticated customer.
   *
   * @param fromDate ISO date — period start (inclusive)
   * @param toDate   ISO date — period end (inclusive)
   * @param filter   "all" or "outstanding_only"
   */
  async generate(
    fromDate: string,
    toDate: string,
    filter: 'all' | 'outstanding_only' = 'all'
  ): Promise<CustomerStatement> {
    const sessionResult = await supabase.auth.getSession();
    const session = sessionResult.data.session;
    if (!session) {
      throw new Error('Not signed in.');
    }
    const customerId = session.user.id;

    // Fetch customer profile for the header.
    const { data: profile, error: profileError } = await supabase
      .from('customer_profile')
      .select('full_name, company, vat_number')
      .eq('id', customerId)
      .maybeSingle();

    if (profileError) {
      throw new Error(`Could not load profile: ${profileError.message}`);
    }

    const customerName =
      profile?.company?.trim() || profile?.full_name?.trim() || session.user.email || 'Account';

    // Step 1: opening balance (sum of all transactions before fromDate).
    const openingBalance = await this.calculateOpeningBalance(customerId, fromDate);

    // Step 2: in-period transactions.
    const transactions = await this.getTransactions(customerId, fromDate, toDate, filter);

    // Step 3: running balance.
    const transactionsWithBalance = this.applyRunningBalance(openingBalance, transactions);

    // Step 4: aging breakdown (as of toDate).
    const aging = await this.getAging(customerId, toDate);

    // Step 5: period totals.
    const closingBalance =
      transactionsWithBalance.length > 0
        ? transactionsWithBalance[transactionsWithBalance.length - 1]!.balanceZAR
        : openingBalance;

    const totalInvoiced = transactions
      .filter((t) => t.kind === 'invoice')
      .reduce((sum, t) => sum + t.debitZAR, 0);

    const totalPaid = transactions
      .filter((t) => t.kind === 'payment')
      .reduce((sum, t) => sum + t.creditZAR, 0);

    const totalCreditNotes = transactions
      .filter((t) => t.kind === 'credit_note')
      .reduce((sum, t) => sum + t.creditZAR, 0);

    return {
      customerId,
      customerName,
      customerVatNumber: profile?.vat_number ?? undefined,
      statementDate: new Date().toISOString(),
      fromDate,
      toDate,
      filter,
      openingBalanceZAR: openingBalance,
      closingBalanceZAR: closingBalance,
      transactions: transactionsWithBalance,
      aging,
      totalInvoicedZAR: totalInvoiced,
      totalPaidZAR: totalPaid,
      totalCreditNotesZAR: totalCreditNotes,
    };
  },

  /**
   * Sum every prior transaction (issued/paid before fromDate). Customer's
   * carried-forward balance.
   */
  async calculateOpeningBalance(customerId: string, fromDate: string): Promise<number> {
    const [invoicesBefore, paymentsBefore, creditsBefore] = await Promise.all([
      supabase
        .from('invoice')
        .select('total, status')
        .eq('customer_id', customerId)
        .lt('issue_date', fromDate)
        .neq('status', 'draft')
        .neq('status', 'cancelled'),
      supabase
        .from('payment')
        .select('amount, paid_at, status')
        .eq('customer_id', customerId)
        .lt('paid_at', fromDate)
        .eq('status', 'success'),
      supabase
        .from('credit_note')
        .select('amount, issued_at')
        .eq('customer_id', customerId)
        .lt('issued_at', fromDate),
    ]);

    if (invoicesBefore.error) throw invoicesBefore.error;
    if (paymentsBefore.error) throw paymentsBefore.error;
    if (creditsBefore.error) throw creditsBefore.error;

    const invoicedBefore = (invoicesBefore.data ?? []).reduce(
      (sum, r) => sum + num(r.total),
      0
    );
    const paidBefore = (paymentsBefore.data ?? []).reduce(
      (sum, r) => sum + num(r.amount),
      0
    );
    const creditedBefore = (creditsBefore.data ?? []).reduce(
      (sum, r) => sum + num(r.amount),
      0
    );

    return invoicedBefore - paidBefore - creditedBefore;
  },

  /** All transactions in [fromDate, toDate], optionally filtered to outstanding. */
  async getTransactions(
    customerId: string,
    fromDate: string,
    toDate: string,
    filter: 'all' | 'outstanding_only'
  ): Promise<StatementTransaction[]> {
    const [invoiceRes, paymentRes, creditRes] = await Promise.all([
      supabase
        .from('invoice')
        .select('id, number, issue_date, due_date, status, total')
        .eq('customer_id', customerId)
        // A QUOTATION is an offer, not a transaction. Without this it appears on
        // the customer's own statement labelled kind:'invoice' — because a
        // quotation in lifecycle_state='pending_payment' carries legacy
        // status='unpaid' via invoice_status_legacy_sync, and this query filters
        // on that legacy column. Found by the 2026-07-29 invoice.status sweep.
        .eq('doc_type', 'invoice')
        .gte('issue_date', fromDate)
        .lte('issue_date', toDate)
        .neq('status', 'draft')
        .neq('status', 'cancelled')
        .order('issue_date', { ascending: true }),
      supabase
        .from('payment')
        .select('id, invoice_id, paid_at, amount, status, gateway, gateway_transaction_id')
        .eq('customer_id', customerId)
        .eq('status', 'success')
        .gte('paid_at', fromDate)
        .lte('paid_at', toDate)
        .order('paid_at', { ascending: true }),
      supabase
        .from('credit_note')
        .select('id, number, issued_at, amount, related_invoice_id, reason')
        .eq('customer_id', customerId)
        .gte('issued_at', fromDate)
        .lte('issued_at', toDate)
        .order('issued_at', { ascending: true }),
    ]);

    if (invoiceRes.error) throw invoiceRes.error;
    if (paymentRes.error) throw paymentRes.error;
    if (creditRes.error) throw creditRes.error;

    const invoices = (invoiceRes.data ?? []) as InvoiceLite[];
    const payments = (paymentRes.data ?? []) as PaymentLite[];
    const credits = (creditRes.data ?? []) as CreditNoteLite[];

    const transactions: StatementTransaction[] = [];

    for (const inv of invoices) {
      if (filter === 'outstanding_only' && (inv.status === 'paid' || inv.status === 'refunded')) {
        continue;
      }
      transactions.push({
        date: inv.issue_date,
        kind: 'invoice',
        reference: inv.number,
        description: `Invoice ${inv.number}`,
        debitZAR: num(inv.total),
        creditZAR: 0,
        balanceZAR: 0, // filled in later
        sourceId: inv.id,
      });
    }

    for (const pay of payments) {
      if (!pay.paid_at) continue;
      transactions.push({
        date: pay.paid_at,
        kind: 'payment',
        reference: pay.gateway_transaction_id ?? pay.id.slice(0, 8),
        description:
          pay.gateway === 'eft_manual'
            ? 'EFT payment'
            : pay.gateway === 'payfast'
              ? 'PayFast payment'
              : pay.gateway === 'credit_balance'
                ? 'Credit balance applied'
                : 'Staff adjustment',
        debitZAR: 0,
        creditZAR: num(pay.amount),
        balanceZAR: 0,
        sourceId: pay.id,
      });
    }

    for (const cn of credits) {
      transactions.push({
        date: cn.issued_at,
        kind: 'credit_note',
        reference: cn.number,
        description: `Credit note: ${cn.reason}`,
        debitZAR: 0,
        creditZAR: num(cn.amount),
        balanceZAR: 0,
        sourceId: cn.id,
      });
    }

    transactions.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    return transactions;
  },

  /** Apply running balance to a sorted transaction list. */
  applyRunningBalance(
    openingBalance: number,
    transactions: StatementTransaction[]
  ): StatementTransaction[] {
    let running = openingBalance;
    return transactions.map((t) => {
      running += t.debitZAR - t.creditZAR;
      return { ...t, balanceZAR: running };
    });
  },

  /**
   * Aging analysis as of `asOfDate` — outstanding invoice balances bucketed
   * by days past due.
   *
   * Bucket boundaries match the IFRS-aligned 6-bucket layout used in
   * Hindsight's TradePayablesReceivablesService.
   */
  async getAging(customerId: string, asOfDate: string): Promise<AgingAnalysis> {
    const { data, error } = await supabase
      .from('invoice')
      .select('id, number, due_date, total, status')
      .eq('customer_id', customerId)
      // Quotations are not receivables and must never age. Same legacy-status
      // trap as the transaction query above (2026-07-29 sweep).
      .eq('doc_type', 'invoice')
      .in('status', ['unpaid', 'partial', 'overdue'])
      .lte('issue_date', asOfDate);

    if (error) {
      throw new Error(`Could not compute aging: ${error.message}`);
    }
    if (!data || data.length === 0) return { ...ZERO_AGING };

    // Outstanding amounts: total - sum(payments) - sum(credit notes against invoice).
    // Simpler approximation here: assume invoice.total is current outstanding when
    // status is unpaid/overdue (most common case). For partial we'd need the
    // remaining-balance computed by summing payments per invoice — Phase 2 polish.
    const aging: AgingAnalysis = { ...ZERO_AGING };
    const asOf = new Date(asOfDate).getTime();

    for (const inv of data) {
      const due = new Date(inv.due_date).getTime();
      const daysPastDue = Math.floor((asOf - due) / 86_400_000);
      const amount = num(inv.total);

      if (daysPastDue <= 0) aging.currentZAR += amount;
      else if (daysPastDue <= 30) aging.days30ZAR += amount;
      else if (daysPastDue <= 60) aging.days60ZAR += amount;
      else if (daysPastDue <= 90) aging.days90ZAR += amount;
      else if (daysPastDue <= 120) aging.days120ZAR += amount;
      else aging.over120ZAR += amount;

      aging.totalZAR += amount;
    }

    return aging;
  },
};
